
import APPS from '@generio-ai/library-legacy/constants/apps'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import VERSION from '@/constants/version.js'
import { requires } from '@/utils/validate.js'


export default {

    async upgradeAsset({ getters, dispatch, rootGetters }, {
        asset
    }) {
        // ensure required parameters are present
        requires(asset)

        // get app and version
        const app = asset.app
        const version = asset.additional?.version || 1

        // only upgrade if necessary
        if (version === VERSION) return

        // only upgrade outdated assets
        if (version > VERSION) {
            dispatch('notifications/show', {
                message: 'Cannot open asset from a newer version.',
                level: LEVELS.ERROR
            }, { root: true })
            return
        }

        // inform about upgrading
        console.log(`Upgrading asset from version ${version} to ${VERSION}`)
        dispatch('overlay/show', {
            message: 'upgrading flow to latest version'
        }, { root: true })

        // get flow
        const flowId = asset?.additional?.flow
        if (!flowId) {
            throw new Error('Asset is corrupt and cannot be upgraded')
        }

        // get all assets for that flow
        const assets = getters.getAssetsForFlow(app, flowId) || []

        // iterate through all assets
        for (const asset of assets) 
        {
            // get current additional
            const outdatedAdditional = asset?.additional || {}

            // upgrade attributes
            const category = upgradeCategory(version, outdatedAdditional?.category)
            const component = upgradeComponent(version, outdatedAdditional?.card?.component)
            const option = upgradeOption(version, outdatedAdditional?.card?.option)
            const action = upgradeAction(version, outdatedAdditional?.card?.action)
            const parents = upgradeParents(version, outdatedAdditional?.parents)
            const ignore = upgradeIgnore(version, outdatedAdditional?.ignore, outdatedAdditional)
            const primitive = upgradePrimitive(version, outdatedAdditional?.primitive, outdatedAdditional)
            const prompt = upgradePrompt(version, app, outdatedAdditional?.prompt, category)

            // create new additional
            const newAdditional = {
                category: category,
                card: {
                    index: outdatedAdditional?.card?.index || 0,
                    component: component,
                    option: option,
                    action: action,
                },
                flow: outdatedAdditional?.flow || null,
                parents: parents,
                ignore: ignore,
                primitive: primitive,
                prompt: prompt,
                rating: outdatedAdditional?.rating || 0,
                skipped: outdatedAdditional?.skipped ?? false,
                version: VERSION,
                upgraded: true
            }

            // upgrade the app if necessary
            if ([APPS.SKETCH, APPS.SKETCH2].includes(app)) {
                await dispatch('assets/updateApp', {
                    asset: asset,
                    app: APPS.CREATE
                }, { root: true })
            }

            // upgrade the new additional information
            await dispatch('assets/updateAdditional', {
                asset: asset,
                additional: newAdditional
            }, { root: true })
        }
    }
}

function upgradeCategory(version, category) {
    switch (category) {
        case 'asset':
        case 'prompt':
            return CATEGORY.PRIMITIVE
        case 'image':
            return CATEGORY.PHOTO
    }
    return category
}

function upgradeComponent(version, component) {
    switch (component) 
    {
        case 'CardSelectImport':
        case 'CardGallery':
            return 'CardImport'
        case 'CardImageBase':
        case 'CardImageCrop':
        case 'CardImageRemove':
            return 'CardImage'
        case 'CardModelBase':
        case 'CardModelSelect':
            return 'CardModel'
        case 'CardSelectPreference':
            return 'CardSelectGeneration'
    }
    return component
}

function upgradeOption(version, option) {
    switch (option) 
    {
        case 'import_image':
            return 'import_upload'
        case 'generation_geometry_only':
            return 'generation_geometry'
        case 'generation_textured_model':
        case 'generation_optimized_model':
            return 'generation_textured'
    }
    return option
}

function upgradeAction(version, action) {
    return action
}

function upgradeParents(version, parents) {
    if (parents === null || parents === undefined) return null
    if (!Array.isArray(parents)) 
        return Object.values(parents)
    return [...parents]
}

function upgradeIgnore(version, ignore, additional) {
    if (ignore === undefined) return !additional?.active ?? false
    return ignore
}

function upgradePrimitive(version, primitive, additional) {
    if (primitive === undefined) return additional?.cloned ?? false
    return primitive
}

function upgradePrompt(version, app, prompt, category) {
    const user = prompt?.user || prompt?.positive || ""
    const quality = app === APPS.SKETCH || app === APPS.SKETCH2 ? "legacy" : (prompt?.quality || "low")
    return {
        user: user,
        system: prompt?.system || user,
        augmentation: prompt?.augmentation || {},
        balance: prompt?.balance || 0.5,
        dual: prompt?.dual || category === CATEGORY.MODEL ? true : false,
        quality: quality,
        solid: prompt?.solid ?? true
    }
}